import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import PresentationSlide10 from './PresentationSlide10';
import PresentationSlide11 from './PresentationSlide11';
import PresentationSlide12 from './PresentationSlide12';
import PresentationSlide12A from './PresentationSlide12A';
import PresentationSlide13 from './PresentationSlide13';
import PresentationSlide14 from './PresentationSlide14';
import PresentationSlide15 from './PresentationSlide15';
import PresentationSlide16 from './PresentationSlide16';
import PresentationSlide17 from './PresentationSlide17';
import PresentationSlide18 from './PresentationSlide18';
import PresentationSlide19 from './PresentationSlide19';
import PresentationSlide20 from './PresentationSlide20';
import PresentationSlide21 from './PresentationSlide21';
import PresentationSlide22 from './PresentationSlide22';
import PresentationSlide22A from './PresentationSlide22A';
import PresentationSlide22B from './PresentationSlide22B';
import PresentationSlide23 from './PresentationSlide23';

// Slides válidos
const VALID_SLIDES = [10, 11, 12, '12a', 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, '22a', '22b', 23];

// Componentes estilizados
const PresentationContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SlideContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  height: 100%;
  overflow: hidden;
`;

const AnimatedSlideContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const NavigationControls = styled.div`
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  padding: 12px 20px;
  border-radius: 15px;
  margin: 0 auto;
  width: fit-content;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 182, 255, 0.3);
`;

const NavButton = styled.button`
  background: rgba(0, 150, 255, 0.3);
  color: #64B6FF;
  border: 1px solid #64B6FF;
  border-radius: 5px;
  padding: 8px 15px;
  cursor: pointer;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #64B6FF);
    animation: scanline 2s linear infinite;
  }
  
  &:hover {
    background: rgba(0, 150, 255, 0.5);
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.7);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @keyframes scanline {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const SlideIndicator = styled.div`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  font-size: 14px;
  letter-spacing: 1px;
  margin: 0 10px;
`;

// Variantes de animação para transições tecnológicas
const slideVariants = {
  // Entrada vindo da direita (avançar)
  enterFromRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Entrada vindo da esquerda (voltar)
  enterFromLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Estado central (slide atual)
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeOut"
    }
  },
  // Saída para a esquerda (avançar)
  exitToLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeIn"
    }
  },
  // Saída para a direita (voltar)
  exitToRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: {
      type: "tween",
      duration: 0.25,
      ease: "easeIn"
    }
  }
};

// Variante para o efeito de glitch tecnológico - simplificado e acelerado
const glitchAnimation = {
  hidden: {
    clipPath: "inset(100% 0 0 0)",
  },
  visible: {
    clipPath: "inset(0 0 0 0)",
    transition: { 
      duration: 0.25,
      ease: "easeOut"
    }
  },
  exit: {
    clipPath: "inset(0 0 100% 0)",
    transition: { 
      duration: 0.20,
      ease: "easeIn"
    }
  }
};

const PresentationPage = () => {
  const { slideNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detecta se está na rota /presentation ou /apresentacao
  const isEnglishRoute = location.pathname.startsWith('/presentation');
  const baseRoute = isEnglishRoute ? '/presentation' : '/apresentacao';
  
  const parsedSlide = slideNumber !== undefined ? (VALID_SLIDES.includes(slideNumber) ? slideNumber : parseInt(slideNumber)) : 0;
  const [currentSlide, setCurrentSlide] = useState(() => {
    const idx = VALID_SLIDES.indexOf(parsedSlide);
    return idx !== -1 ? idx : 0;
  });
  const [direction, setDirection] = useState("forward"); // Controla a direção da transição
  const noAnim = location.state?.noAnim;

  const [hasInitialized, setHasInitialized] = useState(false);
  const totalSlides = VALID_SLIDES.length;
  // Reset noAnim after first render to allow animations on subsequent slides
  useEffect(() => {
    if (noAnim) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [noAnim, navigate, location.pathname]);
  
  // Atualiza o slide quando o parâmetro da URL muda
  useEffect(() => {
    if (slideNumber) {
      const slideNum = VALID_SLIDES.includes(slideNumber) ? slideNumber : parseInt(slideNumber);
      const newIndex = VALID_SLIDES.indexOf(slideNum);

      if (newIndex !== -1) {
        // Define a direção com base na mudança de slide (comparando índices válidos)
        if (hasInitialized) {
          if (newIndex > currentSlide) {
            setDirection("forward");
          } else if (newIndex < currentSlide) {
            setDirection("backward");
          }
        }
        setCurrentSlide(newIndex);
      } else {
        navigate(`${baseRoute}/10`, { replace: true });
      }
    } else {
      navigate(`${baseRoute}/10`, { replace: true });
    }
    
    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [slideNumber, navigate, currentSlide, hasInitialized, totalSlides, baseRoute]);
  
  // Navegação com teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        handleRightArrow();
      } else if (e.key === 'ArrowLeft') {
        handleLeftArrow();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slideNumber, navigate, baseRoute]);
  
  const handleRightArrow = () => {
    const currentParam = VALID_SLIDES.includes(slideNumber) ? slideNumber : parseInt(slideNumber, 10);
    const idx = VALID_SLIDES.indexOf(currentParam);
    if (idx !== -1 && idx < VALID_SLIDES.length - 1) {
      setDirection("forward");
      const nextParam = VALID_SLIDES[idx + 1];
      const formattedNext = typeof nextParam === 'number' ? (nextParam < 10 ? String(nextParam).padStart(2, '0') : String(nextParam)) : nextParam;
      navigate(`${baseRoute}/${formattedNext}`);
    }
  };

  const handleLeftArrow = () => {
    const currentParam = VALID_SLIDES.includes(slideNumber) ? slideNumber : parseInt(slideNumber, 10);
    const idx = VALID_SLIDES.indexOf(currentParam);
    if (idx > 0) {
      setDirection("backward");
      const prevParam = VALID_SLIDES[idx - 1];
      const formattedPrev = typeof prevParam === 'number' ? (prevParam < 10 ? String(prevParam).padStart(2, '0') : String(prevParam)) : prevParam;
      navigate(`${baseRoute}/${formattedPrev}`);
    }
  };
  
  // Renderiza o slide atual
  const renderCurrentSlide = () => {
    const currentParam = VALID_SLIDES[currentSlide] ?? 10;
    switch (currentParam) {
      case 10:
        return <PresentationSlide10 />;
      case 11:
        return <PresentationSlide11 />;
      case 12:
        return <PresentationSlide12 />;
      case '12a':
        return <PresentationSlide12A />;
      case 13:
        return <PresentationSlide13 />;
      case 14:
        return <PresentationSlide14 />;
      case 15:
        return <PresentationSlide15 />;
      case 16:
        return <PresentationSlide16 />;
      case 17:
        return <PresentationSlide17 />;
      case 18:
        return <PresentationSlide18 />;
      case 19:
        return <PresentationSlide19 />;
      case 20:
        return <PresentationSlide20 />;
      case 21:
        return <PresentationSlide21 />;
      case 22:
        return <PresentationSlide22 />;
      case '22a':
        return <PresentationSlide22A />;
      case '22b':
        return <PresentationSlide22B />;
      case 23:
        return <PresentationSlide23 />;
      default:
        return null;
    }
  };
  
  return (
    <PresentationContainer>
      <SlideContainer>
        { noAnim ? (
          // Render sem animação na transição inicial
          <div style={{ width: '100%', height: '100%' }}>
            {renderCurrentSlide()}
          </div>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
          <AnimatedSlideContainer
            key={currentSlide}
            initial={ noAnim ? false : (currentSlide === 1 && !hasInitialized ? "center" : (direction === "forward" ? "enterFromRight" : "enterFromLeft"))}
            animate="center"
            exit={ noAnim ? undefined : (direction === "forward" ? "exitToLeft" : "exitToRight")}
            variants={ noAnim ? {} : slideVariants}
            transition={ noAnim ? { duration: 0 } : undefined }
          >
            <motion.div
              style={{ width: '100%', height: '100%' }}
              initial={ noAnim ? "visible" : (currentSlide === 1 && !hasInitialized ? "visible" : "hidden") }
              animate="visible"
              exit={ noAnim ? undefined : "exit" }
              variants={ noAnim ? {} : glitchAnimation }
            >
              {renderCurrentSlide()}
            </motion.div>
          </AnimatedSlideContainer>
        </AnimatePresence>
        ) }
      </SlideContainer>

    </PresentationContainer>
  );
};

export default PresentationPage;
